import React from "react";
import styled from "styled-components";

import { colors } from "../../utils";
import { focusRing } from "../../theme/tokens";

/**
 * CameraPicker — "pick an external/USB camera instead of only the default"
 * (QA-105).
 *
 * A native `<select>`, deliberately. It is one short list of plain strings; a
 * native select is keyboard-navigable, screen-reader-announced and
 * touch-friendly for free, and every custom dropdown in this tree had to
 * re-earn all three. A visible `<label>` rather than a placeholder option,
 * because the user must be able to tell what the control does while a value is
 * already selected.
 *
 * Renders nothing when there is only one camera: a picker with a single option
 * is a control that cannot do anything, and it would sit between the user and
 * the shutter button on a small screen.
 */

const Wrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 420px;
  margin-bottom: 8px;
`;

const Label = styled.label`
  font: 500 12px "DM Sans", system-ui, sans-serif;
  color: ${colors.textGrey};
  white-space: nowrap;
`;

const Select = styled.select`
  flex: 1;
  min-width: 0;
  height: 32px;
  padding: 0 8px;
  border: 1px solid ${colors.lightBorder};
  border-radius: 8px;
  background: ${colors.white};
  color: ${colors.black};
  font: 500 13px "DM Sans", system-ui, sans-serif;
  cursor: pointer;

  &:focus-visible {
    /* Reads the focus-ring token, not a raw palette entry (focusRingHardcodeRatchet.test.js) —
       colors.PrimaryPurple500 happens to equal the same #9B5AF8 today but would not follow the
       token if it ever moves again. */
    outline: ${focusRing.width}px solid ${focusRing.color};
    outline-offset: ${focusRing.innerWidth}px;
  }

  &:disabled {
    cursor: not-allowed;
    color: ${colors.textGrey};
  }
`;

const CameraPicker = ({
  cameras = [],
  selectedId,
  onSelect,
  labelFor,
  disabled = false,
  id = "capture-camera-picker",
}) => {
  if (!cameras || cameras.length < 2) return null;

  return (
    <Wrap data-testid="camera-picker">
      <Label htmlFor={id}>Camera</Label>
      <Select
        id={id}
        value={selectedId || ""}
        disabled={disabled}
        onChange={(e) => onSelect && onSelect(e.target.value)}
      >
        {cameras.map((device, index) => (
          <option key={device.deviceId} value={device.deviceId}>
            {labelFor ? labelFor(device, index) : device.label || `Camera ${index + 1}`}
          </option>
        ))}
      </Select>
    </Wrap>
  );
};

export default CameraPicker;
